import { apiService } from "@/services";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { GetKpsRecalculationResponse, KpsRecalculationRun } from "@/types/kps-recalculation";

// Global, not tenant-scoped (`components/keyword.md § The two new routes`) -- there is no
// `organization_id` on `configuration_settings` and no per-brand run, so this key is not keyed on
// `selectedBrandId` (`constraints.md § C-16` applies only to brand-scoped data; this genuinely
// is not).
export const kpsRecalculationQueryKey = () => ["kps-recalculation"] as const;

const POLL_INTERVAL_MS = 5000;

/**
 * `GET /api/kps-recalculation` (`components/keyword.md § GET /api/kps-recalculation`,
 * `decisions/kps-recalculation-progress-is-polled.md`). Polled every 5s by every admin client on
 * every screen while `enabled`. AUT-281: the endpoint no longer issues a warehouse query -- it is
 * one indexed MySQL row read -- so the cost that used to justify a 10s interval is gone, and a
 * sub-minute run (~8.7s at ~600k rows) polled every 10s would report its own completion a quarter
 * of a lifetime late. `refetchIntervalInBackground: false` so a backgrounded tab stops polling
 * rather than hammering the endpoint from every open tab.
 */
export const useGetKpsRecalculation = (enabled: boolean) =>
  useQuery<GetKpsRecalculationResponse>({
    queryKey: kpsRecalculationQueryKey(),
    queryFn: () =>
      apiService.get<GetKpsRecalculationResponse, undefined>("/kps-recalculation"),
    enabled,
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });

/**
 * `POST /api/kps-recalculation` (`components/keyword.md § POST /api/kps-recalculation`). Empty
 * body -- the server decides for itself whether a run is warranted. Deliberately has no `onError`:
 * the 409/422/500 bodies all carry a distinct, non-empty `message` (`constraints.md § C-22`) that
 * the caller (the confirmation flow in WEB-002) needs to read and render itself, so the error is
 * left to propagate rather than being swallowed into a generic toast here.
 */
export const useStartKpsRecalculation = () =>
  useMutation<KpsRecalculationRun, unknown, void>({
    mutationFn: () =>
      apiService.post<KpsRecalculationRun, Record<string, never>>(
        "/kps-recalculation",
        {},
      ),
  });
